import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, AlertTriangle, Camera, Scale, Globe, Shield, Users, CheckCircle } from 'lucide-react';

export default function Terms() {
  return (
    <Layout>
      <PageMeta
        title="Terms and Conditions | ListBright"
        description="Read the ListBright Terms and Conditions covering account use, content rights, AI-generated outputs, billing and acceptable use of our listing tools."
        breadcrumbs={[{ label: 'Terms', path: '/terms' }]}
      />
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <FileText className="w-8 h-8 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-4">Terms and Conditions</h1>
            <p className="text-muted-foreground">
              Last updated: January 9, 2026
            </p>
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            {/* Introduction */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="w-5 h-5 text-primary" />
                  1. Introduction and Acceptance
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>
                  Welcome to ListBright ("we," "our," or "us"). These Terms and Conditions ("Terms") govern 
                  your access to and use of our AI-powered property listing platform, including our website, 
                  services, and any related applications (collectively, the "Service").
                </p>
                <p>
                  By creating an account or using our Service, you agree to be bound by these Terms. If you 
                  do not agree to these Terms, please do not use our Service.
                </p>
              </CardContent>
            </Card>

            {/* AI Photo Enhancement & Virtual Staging - CRITICAL SECTION */}
            <Card className="border-amber-200 bg-amber-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="w-5 h-5" />
                  2. AI-Enhanced Photos & Virtual Staging Disclosure Requirements
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <div className="bg-white rounded-lg p-4 border border-amber-200 mb-4">
                  <p className="font-semibold text-amber-800 mb-2">
                    ⚠️ IMPORTANT: Legal Disclosure Requirements
                  </p>
                  <p className="text-sm">
                    By using ListBright's AI photo enhancement and virtual staging features, you acknowledge 
                    and agree to comply with all applicable laws, regulations, and industry standards regarding 
                    the disclosure of digitally altered or virtually staged property images.
                  </p>
                </div>

                <h4 className="font-semibold mt-4">2.1 Mandatory Disclosure Obligations</h4>
                <p>
                  When using AI-enhanced photos or virtually staged images created through our Service, 
                  <strong> you must clearly disclose</strong> to potential buyers, renters, and any third 
                  parties that:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Images have been digitally enhanced or altered</strong> using artificial 
                    intelligence technology
                  </li>
                  <li>
                    <strong>Virtual staging has been applied</strong> – furniture, decorations, and other 
                    items shown in photos may not be included with the property and do not physically exist 
                    at the property
                  </li>
                  <li>
                    <strong>Sky replacements, lighting adjustments, and other visual modifications</strong> 
                    have been made to enhance the appearance of the property
                  </li>
                  <li>
                    <strong>Structural modifications shown (if any)</strong> are for visualization purposes 
                    only and do not represent the actual current condition of the property
                  </li>
                </ul>

                <h4 className="font-semibold mt-6">2.2 Global Regulatory Compliance</h4>
                <p>
                  Virtual staging and AI photo enhancement in real estate is subject to various regulations 
                  worldwide. You are responsible for understanding and complying with the specific requirements 
                  in your jurisdiction. Key regulations include:
                </p>

                <div className="grid gap-4 mt-4">
                  <div className="bg-white rounded-lg p-4 border">
                    <h5 className="font-semibold flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-500" />
                      United States
                    </h5>
                    <ul className="text-sm mt-2 space-y-1">
                      <li>• <strong>NAR (National Association of Realtors) Code of Ethics:</strong> Article 12 requires truthful representation of properties</li>
                      <li>• <strong>FTC Guidelines:</strong> Prohibit deceptive advertising practices, including misleading property images</li>
                      <li>• <strong>MLS Rules:</strong> Most Multiple Listing Services require clear disclosure of virtually staged photos</li>
                      <li>• <strong>State Real Estate Laws:</strong> Many states require disclosure of any material alterations to listing photos</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 border">
                    <h5 className="font-semibold flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-500" />
                      European Union
                    </h5>
                    <ul className="text-sm mt-2 space-y-1">
                      <li>• <strong>Consumer Rights Directive:</strong> Requires accurate representation of products and services</li>
                      <li>• <strong>Unfair Commercial Practices Directive:</strong> Prohibits misleading representations that could affect consumer decisions</li>
                      <li>• <strong>GDPR Compliance:</strong> AI-processed images must comply with data protection regulations</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 border">
                    <h5 className="font-semibold flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-500" />
                      United Kingdom
                    </h5>
                    <ul className="text-sm mt-2 space-y-1">
                      <li>• <strong>Consumer Protection from Unfair Trading Regulations 2008:</strong> Prohibits misleading advertising</li>
                      <li>• <strong>Property Misdescriptions Act:</strong> Requires accurate property descriptions and representations</li>
                      <li>• <strong>Estate Agents Act 1979:</strong> Mandates honest and accurate property marketing</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 border">
                    <h5 className="font-semibold flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-500" />
                      Australia & New Zealand
                    </h5>
                    <ul className="text-sm mt-2 space-y-1">
                      <li>• <strong>Australian Consumer Law:</strong> Prohibits false or misleading representations in property sales</li>
                      <li>• <strong>Real Estate Institute Guidelines:</strong> Require clear disclosure of enhanced or staged images</li>
                      <li>• <strong>Fair Trading Act (NZ):</strong> Mandates truthful advertising in real estate</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 border">
                    <h5 className="font-semibold flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-500" />
                      Canada
                    </h5>
                    <ul className="text-sm mt-2 space-y-1">
                      <li>• <strong>Competition Act:</strong> Prohibits false or misleading representations</li>
                      <li>• <strong>CREA (Canadian Real Estate Association) Rules:</strong> Require accurate property representations</li>
                      <li>• <strong>Provincial Real Estate Acts:</strong> Various provinces have specific disclosure requirements</li>
                    </ul>
                  </div>
                </div>

                <h4 className="font-semibold mt-6">2.3 Best Practices for Disclosure</h4>
                <p>To ensure compliance, we recommend the following disclosure practices:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    Include a <strong>visible disclaimer</strong> on all listing pages containing enhanced or 
                    virtually staged photos
                  </li>
                  <li>
                    Add a <strong>watermark or label</strong> to individual images indicating "Virtually Staged," 
                    "AI Enhanced," or "Digitally Modified"
                  </li>
                  <li>
                    Include disclosure language in <strong>property descriptions</strong> and marketing materials
                  </li>
                  <li>
                    Provide <strong>unaltered original photos</strong> upon request from potential buyers or renters
                  </li>
                  <li>
                    Ensure <strong>MLS listings</strong> are marked with appropriate virtual staging indicators 
                    as required by your local MLS rules
                  </li>
                </ul>

                <h4 className="font-semibold mt-6">2.4 Prohibited Uses</h4>
                <p>You must NOT use our AI photo enhancement features to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Hide or conceal property defects, damage, or issues</li>
                  <li>Misrepresent the size, layout, or structural elements of a property</li>
                  <li>Remove or hide features that would negatively impact property value</li>
                  <li>Create misleading impressions about the neighborhood or surrounding area</li>
                  <li>Deceive potential buyers or renters about the actual condition of the property</li>
                </ul>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                  <p className="font-semibold text-red-800">
                    ⚠️ Liability Notice
                  </p>
                  <p className="text-sm text-red-700 mt-2">
                    Failure to properly disclose the use of AI-enhanced or virtually staged images may result 
                    in legal consequences, including fines, license revocation, and civil liability. ListBright 
                    is not responsible for any legal issues arising from improper disclosure or misuse of 
                    enhanced images by users.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* User Responsibilities */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  3. User Responsibilities
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>As a user of ListBright, you agree to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate and truthful information about properties you list</li>
                  <li>Comply with all applicable local, state, national, and international laws and regulations</li>
                  <li>Maintain appropriate real estate licenses where required</li>
                  <li>Not use the Service for any fraudulent or deceptive purposes</li>
                  <li>Properly disclose any AI enhancements or virtual staging as outlined in Section 2</li>
                  <li>Respond promptly to any complaints or inquiries regarding your listings</li>
                </ul>
              </CardContent>
            </Card>

            {/* Intellectual Property */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="w-5 h-5 text-primary" />
                  4. Intellectual Property & Content Rights
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>
                  <strong>Your Content:</strong> You retain ownership of the original photos and content you 
                  upload to ListBright. By uploading content, you grant us a non-exclusive license to process, 
                  enhance, and display your content as part of the Service.
                </p>
                <p>
                  <strong>AI-Generated Content:</strong> AI-enhanced images, descriptions, and other content 
                  generated by our Service are provided for your use in property marketing, subject to these Terms.
                </p>
                <p>
                  <strong>Our Content:</strong> The Service, including its design, features, and underlying 
                  technology, is owned by ListBright and protected by intellectual property laws.
                </p>
              </CardContent>
            </Card>

            {/* Privacy & Data */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  5. Privacy & Data Protection
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>
                  Your privacy is important to us. Please review our <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a> for 
                  information about how we collect, use, and protect your data.
                </p>
                <p>
                  By using our Service, you consent to the processing of your data, including photos uploaded 
                  for AI enhancement, in accordance with our Privacy Policy and applicable data protection laws.
                </p>
              </CardContent>
            </Card>

            {/* Marketing Communications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  6. Marketing Communications
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>
                  If you opt-in to receive marketing communications from us, you consent to receiving:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Product updates and new feature announcements</li>
                  <li>Tips and best practices for property marketing</li>
                  <li>Industry news and regulatory updates</li>
                  <li>Promotional offers and special discounts</li>
                </ul>
                <p>
                  You can unsubscribe from marketing communications at any time by clicking the "unsubscribe" 
                  link in any email or by updating your preferences in your account settings.
                </p>
              </CardContent>
            </Card>

            {/* Limitation of Liability */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="w-5 h-5 text-primary" />
                  7. Limitation of Liability
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, LISTBRIGHT SHALL NOT BE LIABLE FOR ANY INDIRECT, 
                  INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
                </p>
                <p>
                  We are not liable for any legal consequences arising from:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your failure to properly disclose the use of AI-enhanced or virtually staged images</li>
                  <li>Misrepresentation of property conditions or features</li>
                  <li>Violation of applicable real estate laws and regulations</li>
                  <li>Any disputes between you and property buyers, renters, or other third parties</li>
                </ul>
              </CardContent>
            </Card>

            {/* Termination */}
            <Card>
              <CardHeader>
                <CardTitle>8. Termination</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>
                  We reserve the right to suspend or terminate your account if you violate these Terms, 
                  engage in fraudulent or deceptive practices, or use our Service in a manner that could 
                  harm other users or our reputation.
                </p>
              </CardContent>
            </Card>

            {/* Changes to Terms */}
            <Card>
              <CardHeader>
                <CardTitle>9. Changes to These Terms</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>
                  We may update these Terms from time to time. We will notify you of any material changes 
                  by posting the new Terms on this page and updating the "Last updated" date. Your continued 
                  use of the Service after any changes constitutes acceptance of the new Terms.
                </p>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card>
              <CardHeader>
                <CardTitle>10. Contact Us</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>
                  If you have any questions about these Terms, please contact us at:
                </p>
                <ul className="list-none pl-0 space-y-1">
                  <li>Email: <a href="mailto:sales@listbright.ai" className="text-primary hover:underline">sales@listbright.ai</a></li>
                  <li>Support: <a href="/contact" className="text-primary hover:underline">Contact Page</a></li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Footer */}
          <div className="text-center mt-12 pt-8 border-t">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} ListBright. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
